import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production'
  ? 'https://labs.xmacna.ai/strategy-api'
  : 'http://localhost:3003/api';

interface PartenonData {
  totals: {
    today: string;
    yesterday: string;
    week: string;
    month: string;
    total: string;
    unique_users: string;
    total_tool_calls: string;
    total_cost: string;
  };
  hourly: Array<{ hora: number; total: string }>;
  topBots: Array<{
    bot_name: string;
    total_interactions: string;
    total_tool_calls: string;
    unique_users: string;
    models: string[];
    total_cost: string;
    last_interaction: string;
  }>;
  tools: Array<{ tool_name: string; usage_count: string }>;
  models: Array<{ model: string; total: string; total_tokens: string }>;
  daily: Array<{ dia: string; total: string; tool_calls: string }>;
}

const COLORS = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe'];
const CHART_COLORS = {
  primary: '#8b5cf6',
  secondary: '#6366f1',
  accent: '#a78bfa',
  success: '#22c55e',
  warning: '#f59e0b',
};

const PartenonTab: React.FC = () => {
  const [data, setData] = useState<PartenonData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/partenon-dash/initial-load`);
        if (response.data.success) {
          setData(response.data);
        } else {
          setError('Falha ao carregar dados');
        }
      } catch (err: any) {
        setError(err.message || 'Erro de conexao');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-gray-800/50 rounded-xl h-28 border border-gray-700/50" />
          ))}
        </div>
        <div className="bg-gray-800/50 rounded-xl h-72 border border-gray-700/50" />
        <div className="bg-gray-800/50 rounded-xl h-64 border border-gray-700/50" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-6 text-center">
        <p className="text-red-400 text-lg font-medium">Erro ao carregar dados Partenon</p>
        <p className="text-gray-400 mt-2">{error}</p>
      </div>
    );
  }

  const { totals, hourly, topBots, tools, models, daily } = data;

  // Format hourly data (fill missing hours)
  const hourlyData = Array.from({ length: 24 }, (_, h) => {
    const found = hourly.find(item => item.hora === h);
    return { hora: `${h}h`, total: found ? parseInt(found.total) : 0 };
  });

  // Format daily data
  const dailyData = daily.map(d => ({
    dia: new Date(d.dia).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
    total: parseInt(d.total),
    tool_calls: parseInt(d.tool_calls) || 0,
  }));

  // Format tools for pie chart
  const toolsData = tools.filter(t => t.tool_name && t.tool_name.trim()).map(t => ({
    name: t.tool_name.trim(),
    value: parseInt(t.usage_count),
  }));

  // Format models for pie chart
  const modelsData = models.map(m => ({
    name: m.model.replace('gpt-5.2-chat-latest', 'GPT-5.2').replace('claude-sonnet-4.5', 'Sonnet 4.5'),
    value: parseInt(m.total),
  }));

  const formatNumber = (val: string | number) => {
    const n = typeof val === 'string' ? parseInt(val) : val;
    return isNaN(n) ? '0' : n.toLocaleString('pt-BR');
  };

  const formatCost = (val: string | number) => {
    const n = typeof val === 'string' ? parseFloat(val) : val;
    return isNaN(n) ? 'R$ 0,00' : `R$ ${n.toFixed(2).replace('.', ',')}`;
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6">
      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCardSimple
          title="Total Interacoes"
          value={formatNumber(totals.total)}
          subtitle={`Hoje: ${formatNumber(totals.today)} | Ontem: ${formatNumber(totals.yesterday)}`}
          color="purple"
        />
        <MetricCardSimple
          title="Tool Calls"
          value={formatNumber(totals.total_tool_calls)}
          subtitle={`${toolsData.length} ferramentas distintas`}
          color="blue"
        />
        <MetricCardSimple
          title="Usuarios Unicos"
          value={formatNumber(totals.unique_users)}
          subtitle={`${topBots.length} bots ativos`}
          color="green"
        />
        <MetricCardSimple
          title="Custo Total"
          value={formatCost(totals.total_cost)}
          subtitle={`Semana: ${formatNumber(totals.week)} | Mes: ${formatNumber(totals.month)}`}
          color="amber"
        />
      </div>

      {/* Hourly Chart */}
      <div className="bg-gray-800/40 rounded-xl p-5 border border-gray-700/50">
        <h3 className="text-gray-200 font-medium mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Interacoes por Hora (Hoje)
        </h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={hourlyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="hora" stroke="#9ca3af" fontSize={12} />
            <YAxis stroke="#9ca3af" fontSize={12} allowDecimals={false} />
            <Tooltip
              contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
              labelStyle={{ color: '#e5e7eb' }}
              itemStyle={{ color: '#a78bfa' }}
            />
            <Bar dataKey="total" fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} name="Interacoes" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Top Bots Table */}
      <div className="bg-gray-800/40 rounded-xl p-5 border border-gray-700/50">
        <h3 className="text-gray-200 font-medium mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
          Top Bots - Partenon
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-gray-400 border-b border-gray-700">
              <tr>
                <th className="pb-3 pl-2">#</th>
                <th className="pb-3">Bot</th>
                <th className="pb-3 text-right">Interacoes</th>
                <th className="pb-3 text-right">Tool Calls</th>
                <th className="pb-3 text-right">Usuarios</th>
                <th className="pb-3">Modelo</th>
                <th className="pb-3 text-right">Custo</th>
                <th className="pb-3">Ultima Atividade</th>
              </tr>
            </thead>
            <tbody>
              {topBots.map((bot, idx) => (
                <tr key={bot.bot_name} className="border-b border-gray-700/50 hover:bg-gray-700/20 transition-colors">
                  <td className="py-3 pl-2 text-gray-500">{idx + 1}</td>
                  <td className="py-3">
                    <span className="text-gray-200 font-medium">{bot.bot_name}</span>
                  </td>
                  <td className="py-3 text-right text-gray-200 font-mono">{formatNumber(bot.total_interactions)}</td>
                  <td className="py-3 text-right text-gray-300 font-mono">{formatNumber(bot.total_tool_calls)}</td>
                  <td className="py-3 text-right text-gray-300 font-mono">{formatNumber(bot.unique_users)}</td>
                  <td className="py-3">
                    <span className="inline-block px-2 py-0.5 bg-purple-900/30 text-purple-300 rounded text-xs">
                      {bot.models?.[0]?.replace('gpt-5.2-chat-latest', 'GPT-5.2').replace('claude-sonnet-4.5', 'Sonnet 4.5') || '-'}
                    </span>
                  </td>
                  <td className="py-3 text-right text-gray-300">{formatCost(bot.total_cost)}</td>
                  <td className="py-3 text-gray-400 text-xs">{formatDate(bot.last_interaction)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Charts Row: Tools + Models */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tools Pie */}
        {toolsData.length > 0 && (
          <div className="bg-gray-800/40 rounded-xl p-5 border border-gray-700/50">
            <h3 className="text-gray-200 font-medium mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Ferramentas Mais Usadas
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={toolsData}
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={false}
                  fontSize={11}
                >
                  {toolsData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                  itemStyle={{ color: '#e5e7eb' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Models Pie */}
        {modelsData.length > 0 && (
          <div className="bg-gray-800/40 rounded-xl p-5 border border-gray-700/50">
            <h3 className="text-gray-200 font-medium mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              Modelos AI
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={modelsData}
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={false}
                  fontSize={11}
                >
                  {modelsData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                  itemStyle={{ color: '#e5e7eb' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Daily Trend */}
      <div className="bg-gray-800/40 rounded-xl p-5 border border-gray-700/50">
        <h3 className="text-gray-200 font-medium mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
          </svg>
          Tendencia Diaria
        </h3>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={dailyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="dia" stroke="#9ca3af" fontSize={12} />
            <YAxis stroke="#9ca3af" fontSize={12} allowDecimals={false} />
            <Tooltip
              contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
              labelStyle={{ color: '#e5e7eb' }}
            />
            <Legend />
            <Line type="monotone" dataKey="total" stroke={CHART_COLORS.primary} strokeWidth={2} dot={{ r: 4 }} name="Interacoes" />
            <Line type="monotone" dataKey="tool_calls" stroke={CHART_COLORS.success} strokeWidth={2} dot={{ r: 3 }} name="Tool Calls" />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Simple metric card (self-contained, no dependency on shared MetricCard component)
const MetricCardSimple: React.FC<{
  title: string;
  value: string;
  subtitle: string;
  color: 'purple' | 'blue' | 'green' | 'amber';
}> = ({ title, value, subtitle, color }) => {
  const colorMap = {
    purple: 'from-purple-900/30 to-purple-800/10 border-purple-700/30 text-purple-400',
    blue: 'from-blue-900/30 to-blue-800/10 border-blue-700/30 text-blue-400',
    green: 'from-green-900/30 to-green-800/10 border-green-700/30 text-green-400',
    amber: 'from-amber-900/30 to-amber-800/10 border-amber-700/30 text-amber-400',
  };

  return (
    <div className={`bg-gradient-to-br ${colorMap[color]} rounded-xl p-4 border`}>
      <p className="text-gray-400 text-sm">{title}</p>
      <p className={`text-2xl font-bold mt-1 ${colorMap[color].split(' ').pop()}`}>{value}</p>
      <p className="text-gray-500 text-xs mt-1">{subtitle}</p>
    </div>
  );
};

export default PartenonTab;
